/*
 * Default: offline mall API; api_base_url switches both endpoints in Liquid.
 * Gateway base ends in /mall; append the API paths below (full path: /mall/mall/lock/...).
 * Recognition: POST /mall/lock/recognize, application/json { image_base64 }.
 * image_base64 is standard Base64 without the data:image/...;base64, prefix.
 * Confirmation: POST /mall/lock/confirm, application/json { lock_key } after a user click.
 * Both return { resultCode: 100, data: ... }. Never infer compatibility from candidates.
 * Images are JPEG/PNG/WebP; limits use decimal MB (1 MB = 1,000,000 bytes).
 * Reject originals whose standard Base64 reaches 10 MB (excluding the data URL prefix).
 * Compress when the complete JSON request reaches 4 MB; uploads must be strictly below 4 MB.
 * CORS belongs to the gateway. Anonymous requests send no credentials or API secrets.
 * Self-check copy/images intentionally come from Liquid, not API self_checks.
 * Manual submission uses Shopify's native contact form and CAPTCHA flow.
 * Only contact[...] text fields are submitted; photos are used for recognition only.
 * The recipient is the store's Sender email configured in Shopify Notifications.
 * Text drafts stay in sessionStorage across redirects and are cleared on success/reset.
 * Explicit demo_mode uses placeholder candidates and NEVER sends network requests.
 */
(() => {
  if (customElements.get('lock-ultra-ai')) return;
  const MAX_SOURCE_BASE64_BYTES = 10 * 1000 * 1000;
  const MAX_UPLOAD_REQUEST_BYTES = 4 * 1000 * 1000;
  const IMAGE_REQUEST_OVERHEAD_BYTES = JSON.stringify({ image_base64: '' }).length;
  const IMAGE_TOO_LARGE_MESSAGE = '写真のサイズが大きすぎます。小さい画像を選ぶか、撮り直してください。';
  // Standard Base64 encodes every three input bytes as four ASCII bytes, including padding.
  const getBase64ByteLength = (byteLength) => 4 * Math.ceil(byteLength / 3);
  const LOCK_KEY = /^[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+$/;
  const LOCK_TYPES = { case_lock: 'ケースロック', surface_box: '面付箱錠', push_pull: 'プッシュプル錠' };
  const VERDICTS = { compatible: 'ロックUltraを\n取り付けられます', needs_measurement: '鍵の寸法をご確認ください', undetermined: '取り付け可否を確認できませんでした' };
  const RESULT_MESSAGES = {
    needs_measurement: '取り付け可否を確認するため、鍵のタイプを選び、寸法を入力してください。サポートからメールでご案内します。',
    undetermined: '写真だけでは取り付け可否を確認できませんでした。鍵のタイプと寸法を入力してサポートに確認するか、写真を撮り直してください。'
  };

  class LockUltraAI extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.demo = this.dataset.demo === 'true';
      this.form = this.querySelector('form');
      this.manualType = '';
      this.draftKey = `lock-ultra-contact:${window.location.pathname}:${this.id}`;
      this.dialog = this.querySelector('dialog');
      this.photo = null;
      this.candidates = [];
      this.cooldowns = this.cooldowns || {};
      this.recognitionRequestId = '';
      this.confirmationRequestId = '';
      this.busy = false;
      this.operation = (this.operation || 0) + 1;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-file]')) this.selectPhoto(event.target);
        if (event.target.matches('input[type="radio"]')) this.validateField(event.target.closest('[data-field]'));
      }, options);
      this.form.addEventListener('submit', (event) => this.submit(event), { ...options, capture: true });
      this.form.addEventListener('focusout', (event) => {
        const field = event.target.closest('[data-field]');
        if (field && event.target.value.trim()) this.validateField(field);
      }, options);
      this.form.addEventListener('input', (event) => {
        const field = event.target.closest('[data-field]');
        if (field?.querySelector('[aria-invalid="true"]')) this.validateField(field);
        this.message('[data-submit-error]', '');
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, options);
      this.renderCandidates();
      this.setManualType('', { restore: true });
      this.restoreSubmission();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      this.operation += 1;
      this.initialized = false;
    }

    message(selector, text) {
      const element = this.querySelector(selector);
      element.textContent = text;
      element.hidden = !text;
    }

    show(screen) {
      const target = this.querySelector(`[data-screen="${screen}"]`);
      if (!target) return;
      this.querySelectorAll('[data-screen]').forEach((element) => { element.hidden = element !== target; });
      this.querySelector('.lock-ai__shell').scrollTop = 0;
      const title = target.querySelector('.lock-ai__title');
      title.focus({ preventScroll: true });
      this.scrollIntoView({ block: 'start', behavior: 'instant' });
    }

    setBusy(busy, phase) {
      this.busy = busy;
      this.querySelectorAll('[data-action]:not([data-action="measure"]):not([data-action="close-measure"]), [data-submit], .lock-ai__candidate').forEach((button) => { button.disabled = busy; });
      this.querySelector('[data-screen="upload"]').setAttribute('aria-busy', String(busy && phase === 'recognition'));
      this.querySelector('[data-loading]').hidden = !(busy && phase === 'recognition');
      this.querySelector('[data-screen="candidates"]').setAttribute('aria-busy', String(busy && phase === 'confirmation'));
      this.form.setAttribute('aria-busy', String(busy && phase === 'submission'));
      this.querySelector('[data-submit]').textContent = busy && phase === 'submission' ? '送信中…' : '送信する';
    }

    onClick(event) {
      const trigger = event.target.closest('[data-action]');
      if (!trigger || !this.contains(trigger)) return;
      const action = trigger.dataset.action;
      if (action === 'measure') { this.dialog.showModal(); return; }
      if (action === 'close-measure') { this.dialog.close(); return; }
      if (this.busy) return;
      if (action === 'camera' || action === 'album') this.querySelector(`[data-file="${action}"]`).click();
      if (action === 'manual') this.show('manual-type');
      if (action === 'select-manual-type') {
        const type = trigger.dataset.lockType;
        if (type === 'other') {
          this.setManualType('');
          this.clearDraft();
          this.show('unsupported');
        } else if (this.setManualType(type)) {
          this.show('manual');
        }
      }
      if (action === 'retry-recognition' && this.photo) this.recognize(this.photo);
      if (action === 'none') {
        this.confirmationRequestId = '';
        this.setManualType('');
        this.querySelector('[data-contact-context]').value = '該当する鍵の候補がありませんでした。';
        this.show('manual-type');
      }
      if (action === 'restart') this.restart();
    }

    setManualType(type, { restore = false } = {}) {
      const choice = Array.from(this.querySelectorAll('[data-action="select-manual-type"]'))
        .find((button) => button.dataset.lockType === type && type !== 'other');
      const nextType = choice ? type : '';
      if (nextType !== this.manualType) {
        // Dimensions from a different lock type must not carry into this form.
        this.form.querySelectorAll('[data-dimension], [data-field="frame_distance"] input').forEach((input) => {
          if (input.type === 'radio') input.checked = false;
          else input.value = '';
        });
        if (!restore) this.clearDraft();
      }
      this.manualType = nextType;
      const label = choice?.textContent.trim() || '';
      const typeInput = this.querySelector('[data-contact-lock-type]');
      typeInput.value = label;
      typeInput.disabled = !nextType;
      this.querySelector('[data-manual-type-label]').textContent = label || '鍵';
      this.querySelectorAll('[data-manual-for]').forEach((element) => {
        element.hidden = !nextType || !element.dataset.manualFor.split(' ').includes(nextType);
      });
      this.form.querySelectorAll('[data-field]').forEach((field) => {
        const active = Boolean(nextType) && (!field.dataset.manualTypes || field.dataset.manualTypes.split(' ').includes(nextType));
        field.hidden = !active;
        field.querySelectorAll('input').forEach((input) => {
          input.disabled = !active;
          input.required = active;
          input.removeAttribute('aria-invalid');
        });
        const error = field.querySelector('[data-field-error]');
        error.hidden = true;
        error.textContent = '';
      });
      if (!restore) this.message('[data-submit-error]', '');
      return Boolean(nextType);
    }

    async post(endpoint, body, timeout = 30000) {
      const remaining = Math.ceil(((this.cooldowns[endpoint] || 0) - Date.now()) / 1000);
      if (remaining > 0) throw Object.assign(new Error('Rate limited'), { code: 'TOO_MANY_REQUESTS', retryAfter: remaining, retryable: true });
      this.request = new AbortController();
      const request = this.request;
      const timer = setTimeout(() => request.abort(), timeout);
      try {
        const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
        const response = await fetch(endpoint, { method: 'POST', body: JSON.stringify(body), headers, credentials: 'omit', cache: 'no-store', signal: request.signal });
        let retryAfter = 0;
        if (response.status === 429) {
          const raw = response.headers.get('Retry-After');
          const seconds = raw && /^\d+$/.test(raw) ? Number(raw) : Math.ceil((Date.parse(raw) - Date.now()) / 1000);
          retryAfter = Number.isFinite(seconds) && seconds > 0 ? seconds : 60;
          this.cooldowns[endpoint] = Date.now() + retryAfter * 1000;
        }
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.resultCode !== 100) {
          const fallback = { 400: 'INVALID_REQUEST', 401: 'GATEWAY_UNAVAILABLE', 403: 'GATEWAY_UNAVAILABLE', 404: 'GATEWAY_UNAVAILABLE', 413: 'IMAGE_TOO_LARGE', 415: 'UNSUPPORTED_MEDIA_TYPE', 429: 'TOO_MANY_REQUESTS', 502: 'AGENT_CALL_FAILED', 503: 'AGENT_UNAVAILABLE', 504: 'AGENT_TIMEOUT' };
          throw Object.assign(new Error('API request failed'), {
            code: payload?.data?.error_code || fallback[response.status] || 'INVALID_RESPONSE',
            retryable: payload?.data?.retryable ?? [429, 502, 503, 504].includes(response.status),
            requestId: payload?.data?.request_id || response.headers.get('X-Request-ID'), retryAfter
          });
        }
        if (!payload.data || typeof payload.data !== 'object') throw new Error('Invalid response');
        return payload.data;
      } finally {
        clearTimeout(timer);
        if (this.request === request) this.request = null;
      }
    }

    async selectPhoto(input) {
      const file = input.files?.[0];
      input.value = '';
      if (!file || this.busy) return;
      this.message('[data-upload-error]', '');
      this.photo = null;
      this.querySelector('[data-action="retry-recognition"]').hidden = true;
      this.candidates = [];
      this.recognitionRequestId = '';
      this.confirmationRequestId = '';
      this.querySelector('[data-contact-context]').value = '鍵の型番は未確認です。';
      this.renderCandidates();
      this.message('[data-upload-status]', '写真を読み込んでいます…');
      this.setBusy(true, 'recognition');
      const operation = ++this.operation;
      try {
        const photo = await this.preparePhoto(file);
        if (operation !== this.operation) return;
        this.photo = photo;
      } catch (error) {
        if (operation !== this.operation) return;
        this.message('[data-upload-error]', error.userMessage || '写真を読み込めませんでした。JPG、PNG または WebP の写真を選んでください。');
      } finally {
        if (operation === this.operation) {
          this.message('[data-upload-status]', '');
          this.setBusy(false);
        }
      }
      if (operation === this.operation && this.photo) await this.recognize(this.photo);
    }

    async preparePhoto(file) {
      if (!file.size) throw { userMessage: '写真が空です。別の写真を選んでください。' };
      const base64ByteLength = getBase64ByteLength(file.size);
      if (base64ByteLength >= MAX_SOURCE_BASE64_BYTES) throw { userMessage: IMAGE_TOO_LARGE_MESSAGE };
      const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
      const jpeg = bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255;
      const png = [137, 80, 78, 71, 13, 10, 26, 10].every((byte, index) => bytes[index] === byte);
      const webp = String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP';
      if (!jpeg && !png && !webp) throw { userMessage: 'JPG、PNG、WebP に対応しています。HEIC、HEIF、AVIF などは変換してからアップロードしてください。' };
      if (base64ByteLength + IMAGE_REQUEST_OVERHEAD_BYTES < MAX_UPLOAD_REQUEST_BYTES) return file;
      this.message('[data-upload-status]', '写真のサイズを調整しています…');
      const url = URL.createObjectURL(file);
      const image = new Image();
      const canvas = document.createElement('canvas');
      try {
        await new Promise((resolve, reject) => {
          const timer = setTimeout(() => { image.src = ''; reject(new Error('Image decoding timed out')); }, 30000);
          image.onload = () => { clearTimeout(timer); resolve(); };
          image.onerror = () => { clearTimeout(timer); reject(new Error('Image decoding failed')); };
          image.src = url;
        });
        const scale = Math.min(1, 2560 / Math.max(image.naturalWidth, image.naturalHeight));
        let width = Math.max(1, Math.round(image.naturalWidth * scale));
        let height = Math.max(1, Math.round(image.naturalHeight * scale));
        for (let attempt = 0; attempt < 5; attempt += 1) {
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d');
          context.fillStyle = '#fff';
          context.fillRect(0, 0, width, height);
          context.drawImage(image, 0, 0, width, height);
          const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9 - attempt * 0.1));
          if (blob?.size && getBase64ByteLength(blob.size) + IMAGE_REQUEST_OVERHEAD_BYTES < MAX_UPLOAD_REQUEST_BYTES) return new File([blob], 'lock-photo.jpg', { type: 'image/jpeg' });
          width = Math.max(1, Math.round(width * 0.8));
          height = Math.max(1, Math.round(height * 0.8));
        }
        throw new Error('Compression failed');
      } catch {
        throw { userMessage: '写真のサイズを調整できませんでした。小さい画像を選ぶか、撮り直してください。' };
      } finally {
        image.onload = null;
        image.onerror = null;
        URL.revokeObjectURL(url);
        canvas.width = canvas.height = 0;
      }
    }

    readPhotoBase64(photo) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataURL = reader.result;
          resolve(dataURL.slice(dataURL.indexOf(',') + 1));
        };
        reader.onerror = () => reject(reader.error || new Error('Image reading failed'));
        reader.onabort = () => reject(new Error('Image reading aborted'));
        reader.readAsDataURL(photo);
      });
    }

    errorMessage(error, phase) {
      const messages = {
        INVALID_REQUEST: phase === 'confirmation' ? '鍵の情報を確認できませんでした。もう一度写真を選ぶか、サポートへお問い合わせください。' : '写真を読み取れませんでした。撮り直すか、別の写真を選んでください。',
        IMAGE_TOO_LARGE: IMAGE_TOO_LARGE_MESSAGE,
        UNSUPPORTED_MEDIA_TYPE: 'この画像形式には対応していません。JPG、PNG または WebP を選んでください。',
        AGENT_INPUT_REJECTED: '鍵を識別できませんでした。室内側から鍵全体が鮮明に写るよう撮影してください。',
        TOO_MANY_REQUESTS: `アクセスが集中しています。${error.retryAfter || 60} 秒ほど待ってから、もう一度お試しください。`,
        AGENT_AUTH_FAILED: '確認サービスを利用できません。時間をおいてお試しいただくか、サポートへお問い合わせください。',
        AGENT_CALL_FAILED: '確認サービスに接続できませんでした。もう一度お試しいただくか、サポートへお問い合わせください。',
        AGENT_UNAVAILABLE: '確認サービスを利用できません。時間をおいて、もう一度お試しください。',
        AGENT_TIMEOUT: '確認に時間がかかっています。もう一度お試しください。',
        INTERNAL_ERROR: 'サービスに問題が発生しました。時間をおいてお試しいただくか、サポートへお問い合わせください。',
        GATEWAY_UNAVAILABLE: '取り付け確認サービスを利用できません。時間をおいて、もう一度お試しください。',
        FEATURE_DISABLED: '写真による確認サービスは現在ご利用いただけません。時間をおいてお試しください。'
      };
      const message = error.name === 'AbortError' ? '接続がタイムアウトしました。もう一度お試しいただくか、サポートへお問い合わせください。' : messages[error.code] || '確認できませんでした。通信環境を確認してお試しいただくか、サポートへお問い合わせください。';
      return message + (error.requestId ? `（お問い合わせ番号：${error.requestId}）` : '');
    }

    async recognize(photo) {
      if (this.busy) return;
      this.setBusy(true, 'recognition');
      this.message('[data-upload-error]', '');
      this.message('[data-upload-status]', '鍵の種類を確認しています…');
      this.querySelector('[data-action="retry-recognition"]').hidden = true;
      const operation = ++this.operation;
      try {
        const imageBase64 = this.demo ? '' : await this.readPhotoBase64(photo);
        if (operation !== this.operation) return;
        const body = { image_base64: imageBase64 };
        // The key and standard Base64 are ASCII: JSON string length equals UTF-8 bytes.
        // Check the complete request on every attempt, including retries, before calling the API.
        if (JSON.stringify(body).length >= MAX_UPLOAD_REQUEST_BYTES) throw { code: 'IMAGE_TOO_LARGE', retryable: false };
        // Accommodate the documented Web 90s recognition timeout plus transport time.
        const data = this.demo ? {
          status: 'awaiting_confirmation', has_more: false,
          candidates: ['compatible', 'needs_measurement', 'undetermined'].map((verdict, index) => ({
            lock_key: `case_lock/demo_${index + 1}`, lock_type: 'case_lock', model_name: `サンプル ${index + 1}`, demoVerdict: verdict
          }))
        } : await this.post(this.dataset.recognitionEndpoint, body, 100000);
        if (operation !== this.operation) return;
        if (!Array.isArray(data.candidates) || !['awaiting_confirmation', 'undetermined'].includes(data.status)) throw new Error('Invalid recognition response');
        const keys = new Set();
        this.candidates = data.candidates.filter((candidate) => {
          if (!candidate || typeof candidate.lock_key !== 'string' || candidate.lock_key.length > 256 || !LOCK_KEY.test(candidate.lock_key) || keys.has(candidate.lock_key)) return false;
          keys.add(candidate.lock_key);
          return true;
        }).slice(0, 3);
        if (data.candidates.length && !this.candidates.length) throw new Error('Invalid candidates');
        this.recognitionRequestId = data.request_id || '';
        this.renderCandidates();
        if (this.candidates.length) this.show('candidates');
        else this.renderResult({ verdict: 'undetermined', message: data.result?.message || '' });
      } catch (error) {
        if (operation !== this.operation) return;
        this.message('[data-upload-error]', this.errorMessage(error, 'recognition'));
        this.querySelector('[data-action="retry-recognition"]').hidden = error.retryable === false;
      } finally {
        if (operation === this.operation) {
          this.message('[data-upload-status]', '');
          this.setBusy(false);
        }
      }
    }

    imageURL(value) {
      if (typeof value !== 'string' || !value.trim()) return '';
      try {
        const url = new URL(value, window.location.href);
        return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
      } catch { return ''; }
    }

    setImage(image, url, alt, placeholder) {
      const source = this.imageURL(url);
      image.hidden = !source;
      image.alt = alt;
      if (placeholder) placeholder.hidden = Boolean(source);
      image.onerror = () => { image.hidden = true; if (placeholder) placeholder.hidden = false; };
      if (source) image.src = source;
      else image.removeAttribute('src');
    }

    candidateName(candidate) {
      return candidate?.model_name || candidate?.model_choice_id || candidate?.lock_key || '型番未確認';
    }

    renderCandidates() {
      const list = this.querySelector('[data-candidates]');
      list.replaceChildren();
      this.candidates.forEach((candidate) => {
        const card = this.querySelector('[data-candidate-template]').content.firstElementChild.cloneNode(true);
        card.disabled = this.busy;
        const name = this.candidateName(candidate);
        card.querySelector('.lock-ai__candidate-name').textContent = name;
        card.querySelector('.lock-ai__candidate-detail').textContent = LOCK_TYPES[candidate.lock_type] || '鍵のタイプは未確認です';
        this.setImage(card.querySelector('img'), candidate.reference_image_url, name, card.querySelector('.lock-ai__candidate-placeholder'));
        card.addEventListener('click', () => this.selectCandidate(candidate));
        list.append(card);
      });
      this.message('[data-confirm-error]', '');
    }

    async selectCandidate(candidate) {
      if (this.busy) return;
      this.setBusy(true, 'confirmation');
      this.message('[data-confirm-error]', '');
      this.message('[data-confirm-status]', '取り付け可否を確認しています…');
      const operation = ++this.operation;
      try {
        const data = this.demo ? {
          result: { ...candidate, verdict: candidate.demoVerdict, required_accessories: candidate.demoVerdict === 'compatible' ? [] : null }
        } : await this.post(this.dataset.confirmationEndpoint, { lock_key: candidate.lock_key });
        if (operation !== this.operation) return;
        if (!data.result || !Object.hasOwn(VERDICTS, data.result.verdict)) throw new Error('Invalid confirmation response');
        this.confirmationRequestId = data.request_id || '';
        this.renderResult(data.result, candidate);
      } catch (error) {
        if (operation !== this.operation) return;
        this.message('[data-confirm-error]', this.errorMessage(error, 'confirmation') + (error.retryable === false ? '' : ' 鍵の候補をもう一度選んでお試しください。'));
      } finally {
        if (operation === this.operation) {
          this.message('[data-confirm-status]', '');
          this.setBusy(false);
        }
      }
    }

    renderResult(result, candidate = null) {
      const screen = this.querySelector('[data-screen="result"]');
      const compatible = result.verdict === 'compatible';
      const accessories = Array.isArray(result.required_accessories) ? result.required_accessories : null;
      const hasAccessories = Boolean(accessories?.length);
      screen.querySelector('[data-result-title]').textContent = VERDICTS[result.verdict];
      // Keep API prose in the support record; customer-facing status copy is Japanese.
      this.message('[data-result-message]', RESULT_MESSAGES[result.verdict] || '');
      const name = result.model_name || this.candidateName(candidate);
      // The two fixed self-check sections are maintained in Liquid by the merchant.
      screen.querySelector('[data-self-checks]').hidden = !compatible;
      screen.querySelector('[data-result-purchase]').hidden = !compatible;
      screen.querySelector('[data-action="manual"]').hidden = compatible;
      screen.querySelector('[data-result-support]').hidden = result.verdict === 'undetermined' || (compatible && accessories !== null && !hasAccessories);
      screen.querySelector('[data-result-support]').classList.toggle('lock-ai__button--primary', compatible);
      screen.querySelector('[data-result-support]').textContent = compatible ? '必要な付属品をサポートに確認する' : 'サポートに問い合わせる';
      const section = screen.querySelector('[data-accessories-section]');
      // [] is known to need no extra accessories; null/missing is still unknown.
      section.hidden = !compatible || !(hasAccessories || accessories === null || result.show_accessories_section === true);
      screen.querySelector('[data-accessories-title]').textContent = hasAccessories ? '取り付けには、以下のパーツが必要です' : '付属品について';
      this.message('[data-accessories-note]', accessories === null ? '必要な付属品は未確認です。サポートへお問い合わせください。' : hasAccessories ? '' : '追加の付属品は不要です。');
      const list = screen.querySelector('[data-accessories]');
      list.replaceChildren();
      if (accessories) {
        accessories.forEach((accessory) => {
          if (!accessory || typeof accessory !== 'object') return;
          const item = document.createElement('li');
          const title = document.createElement('p');
          const description = document.createElement('p');
          title.textContent = accessory.name || '付属品の情報は未確認です';
          description.textContent = accessory.description || '';
          item.append(title, description);
          list.append(item);
        });
      }
      // The user explicitly chooses a manual type, even after photo recognition.
      this.setManualType('');
      const record = [
        candidate ? `選択された型番：${name}（${candidate.lock_key}）` : '鍵の型番は未確認です。',
        `確認結果：${VERDICTS[result.verdict]}`, result.message || '',
        accessories === null ? '付属品：未確認' : hasAccessories ? `付属品：${accessories.map((item) => item?.name || '未確認').join('、')}` : '付属品：追加の付属品は不要',
        this.recognitionRequestId ? `識別リクエスト番号：${this.recognitionRequestId}` : '',
        this.confirmationRequestId ? `確認リクエスト番号：${this.confirmationRequestId}` : ''
      ];
      this.querySelector('[data-contact-context]').value = record.filter(Boolean).join('\n');
      this.show('result');
    }

    validateField(field) {
      if (!field || field.hidden) return true;
      const inputs = Array.from(field.querySelectorAll('input:not(:disabled)'));
      const first = inputs[0];
      if (!first) return true;
      const value = first.type === 'radio' ? inputs.find((input) => input.checked)?.value || '' : first.value.trim();
      let message = '';
      if (first.type === 'email' && (!value || !first.validity.valid || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))) message = '有効なメールアドレスを入力してください（例：name@example.com）。';
      else if (first.hasAttribute('data-dimension') && (!/^\d+(\.\d+)?$/.test(value) || !Number.isFinite(Number(value)) || Number(value) <= 0)) message = '0 より大きい寸法を mm 単位で入力してください。';
      else if (!value) message = 'つまみの中心からドア枠までの距離を選んでください。';
      const error = field.querySelector('[data-field-error]');
      error.textContent = message;
      error.hidden = !message;
      inputs.forEach((input) => { input.setAttribute('aria-invalid', String(Boolean(message))); });
      return !message;
    }

    restoreSubmission() {
      if (this.demo) return;
      const status = this.querySelector('[data-contact-status]').dataset.contactStatus;
      let draft;
      try { draft = JSON.parse(sessionStorage.getItem(this.draftKey)); } catch { /* Storage may be unavailable. */ }
      if (draft && typeof draft === 'object') {
        // Accept drafts created before the Japanese labels were introduced.
        const legacyTypes = { '圆形锁（嵌入式锁体）': 'case_lock', '箱体锁（明装）': 'surface_box', '推拉式门锁': 'push_pull' };
        const savedType = draft.lockType || legacyTypes[draft['contact[门锁类型]']];
        const choice = Array.from(this.querySelectorAll('[data-action="select-manual-type"]'))
          .find((button) => button.dataset.lockType !== 'other' && (button.dataset.lockType === savedType || button.textContent.trim() === draft['contact[鍵のタイプ]']));
        this.setManualType(choice?.dataset.lockType || '', { restore: true });
        this.form.querySelectorAll('[data-field] input, [data-contact-context]').forEach((input) => {
          if (input.disabled && input.type !== 'email') return;
          let value = draft[input.name];
          if (value === undefined && input.hasAttribute('data-dimension')) {
            const dimension = input.closest('[data-field]').dataset.field;
            value = Object.entries(draft).find(([key]) => key.startsWith(`contact[${dimension}. `))?.[1];
          }
          if (value === undefined && input.type === 'radio') {
            value = { '大于或等于 40 mm': '40 mm以上', '小于 40 mm': '40 mm未満' }[draft['contact[旋钮中心到门框的距离]']];
          }
          if (value === undefined && input.hasAttribute('data-contact-context')) value = draft['contact[识别与确认记录]'];
          if (typeof value !== 'string') return;
          if (input.type === 'radio') input.checked = input.value === value;
          else input.value = value;
        });
      }
      if (status === 'submitted') {
        this.clearDraft();
        this.show('submitted');
      } else if (status === 'error' || draft) {
        this.show(this.manualType ? 'manual' : 'manual-type');
      }
    }

    clearDraft() {
      try { sessionStorage.removeItem(this.draftKey); } catch { /* Storage may be unavailable. */ }
    }

    submit(event) {
      if (this.busy) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      if (!this.manualType || this.querySelector('[data-screen="manual"]').hidden) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.manualType && this.querySelector('[data-screen="unsupported"]').hidden) this.show('manual-type');
        return;
      }
      this.message('[data-submit-error]', '');
      let firstInvalid;
      this.form.querySelectorAll('[data-field]').forEach((field) => {
        if (!this.validateField(field) && !firstInvalid) firstInvalid = field.querySelector('input');
      });
      if (firstInvalid) {
        event.preventDefault();
        event.stopImmediatePropagation();
        firstInvalid.focus();
        return;
      }
      this.form.querySelectorAll('[data-field] input:not([type="radio"]):not(:disabled)').forEach((input) => { input.value = input.value.trim(); });
      if (this.demo) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.show('submitted');
        return;
      }
      const draft = { lockType: this.manualType };
      this.form.querySelectorAll('[data-field] input, [data-contact-context], [data-contact-lock-type]').forEach((input) => {
        if (!input.disabled && (input.type !== 'radio' || input.checked)) draft[input.name] = input.value;
      });
      try { sessionStorage.setItem(this.draftKey, JSON.stringify(draft)); } catch { /* Native submission still works without storage. */ }
      // Allow the browser and Shopify CAPTCHA to submit normally. Do not call
      // form.submit(), fetch /contact, or lock controls while CAPTCHA may defer it.
    }

    restart() {
      this.operation += 1;
      this.request?.abort();
      this.photo = null;
      this.candidates = [];
      this.recognitionRequestId = '';
      this.confirmationRequestId = '';
      this.form.reset();
      this.setManualType('');
      this.querySelector('[data-contact-context]').value = '鍵の型番は未確認です。';
      this.form.querySelectorAll('[data-field] input').forEach((input) => {
        if (input.type === 'radio') input.checked = false;
        else input.value = '';
      });
      this.clearDraft();
      this.querySelector('[data-contact-status]').dataset.contactStatus = '';
      this.querySelectorAll('[data-file]').forEach((input) => { input.value = ''; });
      this.form.querySelectorAll('[aria-invalid]').forEach((input) => input.removeAttribute('aria-invalid'));
      this.querySelector('[data-action="retry-recognition"]').hidden = true;
      this.querySelectorAll('[data-field-error], [data-upload-error], [data-upload-status], [data-submit-error], [data-confirm-error], [data-confirm-status]').forEach((element) => { element.hidden = true; element.textContent = ''; });
      this.renderCandidates();
      this.setBusy(false);
      this.show('upload');
    }
  }

  customElements.define('lock-ultra-ai', LockUltraAI);
})();
